import { TStepContentSectionDto } from "commons/clients";
import {
  parsePhoneNumber,
  isValidPhoneNumber as isValidPhoneNumberMax
} from "react-phone-number-input/input-max";
import {
  ECommunityCode,
  ICheckoutProduct,
  ICommunity,
  IJoiningFeeSummary,
  IPhoneNumber
} from "../types";
import { ICheckoutProductOffer } from "features/checkout/types";
import Nullable = Common.Nullable;

export function prepareStepContent(
  source: TStepContentSectionDto[],
  expectedKeys: string[]
): Record<string, string> {
  const content = source.reduce((acc, section) => {
    section.items.forEach(item => {
      acc[item.key] = item.value;
    });

    return acc;
  }, {} as Record<string, string>);

  const missingKeys = expectedKeys.filter(key => !(key in content));

  if (missingKeys.length) {
    console.warn("Missing content keys:", missingKeys);
  }

  return content;
}

export function isValidPhoneNumber(value: IPhoneNumber) {
  if (!isValidPhoneNumberMax(value.internationalNumber)) {
    return false;
  }

  const phoneNumber = parsePhoneNumber(value.internationalNumber, {
    defaultCountry: value.countryCode,
    defaultCallingCode: value.countryCallingCode,
    extract: false
  });

  if (!phoneNumber || phoneNumber.country !== value.countryCode) {
    return false;
  }

  return phoneNumber.isValid();
}

export function prepareJoiningFee(
  joiningFeeProduct?: ICheckoutProduct
): Nullable<IJoiningFeeSummary> {
  if (!joiningFeeProduct) {
    return null;
  }

  const { netAmount, vat, grossAmount } = joiningFeeProduct.pricingSummary;

  return {
    name: joiningFeeProduct.name,
    productId: joiningFeeProduct.productId,
    productSalesTypeId: joiningFeeProduct.productSalesTypeId,
    netAmount,
    vat,
    grossAmount
  };
}

export function prepareCheckoutProduct(
  checkoutProduct: ICheckoutProduct,
  joiningFeeProduct?: ICheckoutProduct
): ICheckoutProductOffer {
  const { pricingSummary, ...rest } = checkoutProduct;

  return {
    ...pricingSummary,
    ...rest,
    netAmount: pricingSummary.netAmount,
    joiningFee: prepareJoiningFee(joiningFeeProduct)
  };
}

export function calculateTotalVat(
  productVat: Nullable<number>,
  joiningFeeVat: Nullable<number>
): number {
  const vat = productVat || 0;
  const joiningVat = joiningFeeVat || 0;

  return vat + joiningVat;
}

export function calculateTotalAmount(
  grossAmount: Nullable<number>,
  grossJoiningFeeAmount: Nullable<number>
): number {
  const gross = grossAmount || 0;
  const grossJoining = grossJoiningFeeAmount || 0;

  return gross + grossJoining;
}

export function getCommunityCodeById(
  communityAccountId: number,
  activeCommunities: ICommunity[]
): Nullable<ECommunityCode> {
  return activeCommunities.find(c => c.communityAccountId === communityAccountId)?.code ?? null;
}
